import { coronaServices } from "../content/coronaServices.js";

export const SITE_ORIGIN = "https://aicsnetworks.com";
export const SITE_NAME = "AI & Computer Services";
export const DEFAULT_OG_IMAGE = "/logo.png";

/**
 * Canonical NAP (name / address / phone) for AICSNetworks. Single source of
 * truth so the visible footer/contact NAP and the LocalBusiness JSON-LD stay
 * consistent.
 */
export const LOCAL_BUSINESS = {
  name: "AICSNetworks",
  url: SITE_ORIGIN,
  telephone: "+19517201227", // E.164
  phoneDisplay: "(951) 720-1227",
  streetAddress: "420 N McKinley St Suite 11",
  addressLocality: "Corona",
  addressRegion: "CA",
  postalCode: "92860",
  addressCountry: "US",
  // Matches the Google Business Profile hours exactly. Open days only; days not
  // listed are treated as closed (Sat/Sun).
  hoursDisplay: "Monday–Friday, 9 AM–5 PM",
};

/**
 * schema.org LocalBusiness structured data, rendered once on the homepage. Only
 * confirmed facts — no hours, geo, ratings, reviews, services, or sameAs
 * (no real social profiles exist in the codebase yet).
 */
export const localBusinessJsonLd = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  name: LOCAL_BUSINESS.name,
  url: LOCAL_BUSINESS.url,
  telephone: LOCAL_BUSINESS.telephone,
  address: {
    "@type": "PostalAddress",
    streetAddress: LOCAL_BUSINESS.streetAddress,
    addressLocality: LOCAL_BUSINESS.addressLocality,
    addressRegion: LOCAL_BUSINESS.addressRegion,
    postalCode: LOCAL_BUSINESS.postalCode,
    addressCountry: LOCAL_BUSINESS.addressCountry,
  },
  areaServed: { "@type": "City", name: "Corona" },
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "09:00",
      closes: "17:00",
    },
  ],
  description:
    "AICSNetworks provides AI receptionist systems, chatbot development, custom software, business websites, IT support, and network installation for small businesses in Corona, CA.",
};

const publicRoutes = [
  {
    path: "/",
    title:
      "AICSNetworks | AI, Software, Web Development & IT Support in Corona, CA",
    description:
      "AICSNetworks provides AI receptionist systems, chatbot development, custom software, business websites, IT support, and network installation for small businesses in Corona, CA.",
    priority: "1.0",
  },
  {
    path: "/receptionist",
    title: "AI Receptionist Services | AICS",
    description:
      "AI receptionist setup for small businesses: 24/7 call answering, lead qualification, scheduling, SMS, CRM handoffs, and escalation rules.",
    priority: "0.9",
  },
  {
    path: "/ai-chat",
    title: "AI Chat and RAG Assistants | AICS",
    description:
      "Private AI chat assistants grounded in your documents, FAQs, uploads, and business data with controlled handoffs and access boundaries.",
    priority: "0.9",
  },
  {
    path: "/unifi-networks",
    title: "UniFi Network Design and Installation | AICS",
    description:
      "UniFi network design, installation, cabling coordination, guest Wi-Fi, VLAN planning, monitoring, and support for reliable business coverage.",
    priority: "0.9",
  },
  {
    path: "/custom-sites",
    title: "Custom Websites and Launches | AICS",
    description:
      "Fast custom websites and app-backed launch surfaces built around your brand, performance goals, integrations, and deploy workflow.",
    priority: "0.9",
  },
  {
    path: "/contact",
    title: "Contact AICS | Scope an AI, Network, or Web Project",
    description:
      "Tell AICS about your AI receptionist, AI chat, UniFi network, or custom website project and start a scoped discovery conversation.",
    priority: "0.8",
  },
  {
    path: "/pricing",
    title: "Pricing Ranges | AICS",
    description:
      "Review typical starting ranges and budget drivers for AI receptionists, grounded AI chat, UniFi networks, custom websites, and retainers.",
    priority: "0.8",
  },
  {
    path: "/markets",
    title: "Markets and Service Areas | AICS",
    description:
      "AICS serves Southern California businesses in hospitality, retail, professional services, manufacturing, distribution, education, and related sectors.",
    priority: "0.7",
  },
  {
    path: "/contracts",
    title: "Draft Agreement Templates | AICS",
    description:
      "Sample AICS engagement templates for discussion, including draft Master Services Agreement and Digital & AI Implementation SOW language.",
    priority: "0.4",
  },
  {
    path: "/privacy",
    title: "Privacy Policy | AICS",
    description:
      "How AICS collects, uses, and protects your information, including SMS text-messaging programs, A2P campaign registration, and how to opt in and out.",
    priority: "0.3",
  },
  {
    path: "/terms",
    title: "Terms and Conditions | AICS",
    description:
      "The terms governing use of AICS Services, including AI assistant disclaimers, SMS messaging program terms, campaign registration, and opt-in/opt-out.",
    priority: "0.3",
  },
];

const contractRoutes = [
  {
    path: "/contracts/msa",
    title: "Master Services Agreement (draft) | AICS",
    description:
      "General commercial scaffolding for Statements of Work, confidentiality, acceptance, billing, limitation of liability placeholders, termination, dispute venue.",
    priority: "0.3",
  },
  {
    path: "/contracts/sow-digital-ai",
    title: "Statement of Work - Digital & AI Implementation (draft) | AICS",
    description:
      "Broad SOW spanning AI reception workflows, grounded chat/RAG, optional network readiness, and web launch surfaces under one Exhibit.",
    priority: "0.3",
  },
];

const coronaServiceRoutes = coronaServices.map((s) => ({
  path: `/services/${s.slug}`,
  title: s.title,
  description: s.description,
  priority: "0.8",
}));

export const seoRoutes = [
  ...publicRoutes,
  ...contractRoutes,
  ...coronaServiceRoutes,
];

const routeMetaByPath = new Map(seoRoutes.map((route) => [route.path, route]));

export function absoluteUrl(path = "/") {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${SITE_ORIGIN}${normalizedPath}`;
}

export function getSeoMeta(pathname) {
  const exact = routeMetaByPath.get(pathname);
  if (exact) return exact;

  if (pathname.startsWith("/services/")) {
    const slug = pathname.replace("/services/", "");
    const legacyTargets = {
      receptionist: "/receptionist",
      "ai-chat": "/ai-chat",
      "unifi-networks": "/unifi-networks",
      "custom-sites": "/custom-sites",
    };
    return routeMetaByPath.get(legacyTargets[slug]) ?? null;
  }

  if (pathname === "/login") {
    return {
      path: "/login",
      title: `Sign in | ${SITE_NAME}`,
      description: "Sign in to AICSNetworks.",
      noindex: true,
    };
  }

  if (pathname.startsWith("/internal/")) {
    return {
      path: pathname,
      title: `Internal | ${SITE_NAME}`,
      description: "Internal AICS workspace.",
      noindex: true,
    };
  }

  return {
    path: pathname,
    title: `Page not found | ${SITE_NAME}`,
    description: "The requested AICS page could not be found.",
    noindex: true,
  };
}
